import { useState } from 'react';
import { RotterdamForm } from './rotterdam-form.jsx';
import { PESTS } from './data.js';
import { PestDrawing, IconPhone, IconCheck, IconShield } from './icons.jsx';
import { useSEO, buildPlaagSchema, buildBreadcrumb } from './seo.jsx';
import { url } from './url.js';
import { PLAAG_DEEP, getPlaagFaqs } from './plaag-deep.js';
import { AndereDienstenCarousel } from './andere-diensten-carousel.jsx';
import { RatingBar } from './rating-bar.jsx';

function PlaagFaqItem({ q, a, open, onToggle }) {
  return (
    <div className={"faq-item" + (open ? " open" : "")}>
      <button className="faq-question" onClick={onToggle} aria-expanded={open}>
        <span>{q}</span>
        <span className="faq-icon" aria-hidden="true">+</span>
      </button>
      <div className="faq-answer"><p>{a}</p></div>
    </div>
  );
}

export function PlaagPage({ plaag }) {
  const deep = (PLAAG_DEEP && PLAAG_DEEP[plaag.slug]) || null;
  const [openFaq, setOpenFaq] = useState(0);

  // Hero offerte form
  const [hf, setHf] = useState({ naam: "", telefoon: "", postcode: "", ongedierte: plaag.nl });
  const [hfSent, setHfSent] = useState(false);
  const updHf = k => e => setHf(f => ({ ...f, [k]: e.target.value }));
  const submitHero = (e) => { e.preventDefault(); setHfSent(true); };

  // ── SEO ────────────────────────────────────────────────
  const faqSchema = deep && deep.faqs ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: getPlaagFaqs(plaag.slug).map(f => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: [".faq-question", ".faq-answer"],
    },
  } : null;

  useSEO({
    title: deep ? deep.metaTitle : `${plaag.nl} bestrijden, snel en vakkundig`,
    description: deep ? deep.metaDesc : `${plaag.nl} bestrijden in Zuid-Holland? ${plaag.intro.slice(0, 140)}... ✓ Spoeddienst 24/7 ✓ Gecertificeerde partners ✓ Eerlijk tarief vooraf`,
    path: `/${plaag.slug}bestrijding`,
    jsonLd: [
      buildPlaagSchema(plaag),
      buildBreadcrumb([
        { name: "Home", url: "/" },
        { name: "Diensten", url: "/diensten" },
        { name: plaag.nl + "bestrijding", url: `/${plaag.slug}bestrijding` },
      ]),
      ...(faqSchema ? [faqSchema] : []),
    ],
  });

  return (
    <div className="page-anim">
      {/* ─── HERO ─────────────────────────────────────────── */}
      <section className="page-hero">
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="breadcrumb">
            <a href={url.home()}>Home</a>
            <span className="sep">/</span>
            <a href={url.diensten()}>Diensten</a>
            <span className="sep">/</span>
            <span style={{ color: 'rgba(255,255,255,.9)' }}>{plaag.nl}bestrijding</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 48, alignItems: 'center' }} className="ph-grid">
            <div>
              {deep && deep.urgency && (
                <div className="urgency-badge"><span className="ub-pulse"></span> {deep.urgency.replace(/^[^\s]+\s/, '')}</div>
              )}
              <h1 style={{ fontSize: 'clamp(1.7rem,4.2vw,3.2rem)', fontWeight: 900, color: 'white', lineHeight: 1.1, margin: '14px 0 16px', wordBreak: 'normal', overflowWrap: 'break-word' }}>
                {!deep?.hideTitlePrefix && <>{plaag.nl}bestrijding,<br className="hide-mobile"/>{' '}</>}
                {(() => {
                  const sub = deep ? deep.h1Sub.toLowerCase().replace(/\babc\b/g, 'ABC') : 'snel & effectief';
                  const idx = deep?.hideTitlePrefix ? sub.indexOf('ABC') : -1;
                  if (idx > 0) return <>{sub.slice(0, idx)}<span style={{ color: 'var(--orange)' }}>{sub.slice(idx)}</span></>;
                  return <span style={{ color: 'var(--orange)' }}>{sub}</span>;
                })()}
              </h1>
              <p style={{ color: 'rgba(255,255,255,.78)', fontSize: '1.05rem', marginBottom: 24, maxWidth: '54ch' }}>
                {deep ? deep.subkop : plaag.intro}
              </p>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <a href="tel:0855005050" className="btn-bel-lg"><IconPhone width="16" height="16" /> 085 500 50 50</a>
                <a href={url.offerte()} className="btn-form-lg" style={{ borderColor: 'var(--orange)' }}>Gratis offerte aanvragen</a>
              </div>
              {/* Garantie-stamp: alleen voor plagen met echte garantie
                  (wespen 100% seizoensgarantie). Gouden zegel-stijl met
                  pulsing animatie. Direct in beeld = trust + conversie. */}
              {deep && deep.garantieBadge && (
                <div className="garantie-stamp" role="note" aria-label="100 procent seizoensgarantie">
                  <div className="garantie-stamp-icon" aria-hidden="true">100%</div>
                  <div className="garantie-stamp-text">
                    <span className="garantie-stamp-title">
                      {deep.garantieBadgeTitle || '100% Seizoensgarantie'}
                    </span>
                    <span className="garantie-stamp-sub">
                      {deep.garantieBadgeSub || 'Keert het nest binnen het seizoen terug? Wij behandelen kosteloos opnieuw.'}
                    </span>
                  </div>
                </div>
              )}

              {/* Reviews onderaan linker kolom, identiek aan de home-hero
                  positie. Sociale proof na de CTA's, vóór de bezoeker
                  beslist of hij naar het formulier rechts gaat. */}
              <div className="hero-cta-rating">
                <RatingBar />
              </div>
            </div>
            <RotterdamForm variant="hero" />
          </div>
          <style>{`@media (max-width:880px){ .ph-grid{ grid-template-columns: 1fr !important; gap: 32px !important; } }`}</style>
        </div>
      </section>

      {/* ─── DEEP CONTENT (only when PLAAG_DEEP entry exists) ─── */}
      {deep && (
        <>
          {/* Herken je dit */}
          <section className="section">
            <div className="container" style={{ maxWidth: 900 }}>
              <div className="section-tag">Herken je dit?</div>
              <h2 style={{ fontSize: 'clamp(1.6rem,3vw,2.2rem)', fontWeight: 800, color: 'var(--navy)', margin: '12px 0 18px' }}>
                Wanneer is het tijd om een specialist te bellen?
              </h2>
              <p style={{ fontSize: '1.05rem', lineHeight: 1.75, color: 'var(--text)' }}>{deep.recognize}</p>
            </div>
          </section>

          {/* Signalen */}
          <section className="section section-bg">
            <div className="container" style={{ maxWidth: 900 }}>
              <div className="section-tag">Signalen</div>
              <h2 style={{ fontSize: 'clamp(1.6rem,3vw,2.2rem)', fontWeight: 800, color: 'var(--navy)', margin: '12px 0 18px' }}>
                {deep.signalsHead}
              </h2>
              <ul className="checklist" style={{ marginBottom: 22 }}>
                {deep.signalsList.map(s => <li key={s}><IconCheck /> {s}</li>)}
              </ul>
              {deep.entry && (
                <p style={{ fontSize: '.98rem', lineHeight: 1.7, color: 'var(--text-muted)' }}>{deep.entry}</p>
              )}
            </div>
          </section>

          {/* Werkwijze */}
          <section className="section">
            <div className="container">
              <div className="sh">
                <div className="section-tag">Onze werkwijze</div>
                <h2>Zo pakken wij {plaag.nl.toLowerCase()} <em>aan</em></h2>
              </div>
              <div className="process-grid">
                {deep.methodSteps.map((s, i) => {
                  const imgIdx = Math.min(i, 3); // cycle through 4 inspector poses
                  const imgSrc = s.img || `/assets/inspecteur-stap-${imgIdx + 1}.png`;
                  return (
                    <div key={s.t} className="ps">
                      <div className="ps-inspector">
                        <picture>
                          <source srcSet={imgSrc.replace(/\.png$/, '.webp')} type="image/webp" />
                          <img
                            src={imgSrc}
                            alt={`Inspecteur stap ${i + 1}: ${s.t}`}
                            className="ps-inspector-img"
                            loading="lazy"
                            decoding="async"
                            width="220"
                            height="260"
                          />
                        </picture>
                      </div>
                      <h3>{s.t}</h3>
                      <p>{s.d}</p>
                    </div>
                  );
                })}
              </div>
              {deep.methodNote && (
                <div style={{
                  marginTop: 36, padding: 22,
                  background: 'var(--orange-light)', border: '1px solid var(--orange-mid)',
                  borderRadius: 12, textAlign: 'center',
                  color: 'var(--orange-dark)', fontWeight: 600
                }}>
                  ✓ {deep.methodNote}
                </div>
              )}
            </div>
          </section>

          {/* Why pro / gevaren */}
          <section className="section section-navy">
            <div className="container" style={{ maxWidth: 860 }}>
              <div className="section-tag" style={{ background: 'rgba(255,107,43,.18)', color: '#FFB088' }}>Waarom een professional?</div>
              <h2 style={{ color: 'white', margin: '12px 0 18px' }}>
                Doe het <em style={{ color: 'var(--orange)', fontStyle: 'normal' }}>niet zelf</em>
              </h2>
              <p style={{ color: 'rgba(255,255,255,.78)', fontSize: '1.05rem', lineHeight: 1.75 }}>{deep.whyPro}</p>
            </div>
          </section>

          {/* Extra SEO-content secties (per plaag uitgebreid voor #1-ranking) */}
          {deep.extraSections && deep.extraSections.map((s, i) => (
            <section key={i} className={"section" + (i % 2 === 0 ? "" : " section-bg")}>
              <div className="container" style={{ maxWidth: 900 }}>
                <h2 style={{ fontSize: 'clamp(1.5rem,2.8vw,2.1rem)', fontWeight: 800, color: 'var(--navy)', margin: '0 0 18px' }}>
                  {s.h2}
                </h2>
                {s.paragraphs && s.paragraphs.map((p, pi) => (
                  <p key={pi} style={{ fontSize: '1.02rem', lineHeight: 1.75, color: 'var(--text)', marginBottom: 14 }}>
                    {p}
                  </p>
                ))}
              </div>
            </section>
          ))}

          {/* Kosten */}
          <section className="section">
            <div className="container" style={{ maxWidth: 860 }}>
              <div style={{
                display: 'grid', gridTemplateColumns: '1fr 280px', gap: 32, alignItems: 'center'
              }} className="kost-grid">
                <div>
                  <div className="section-tag">Kosten</div>
                  <h2 style={{ fontSize: 'clamp(1.5rem,2.8vw,2rem)', fontWeight: 800, color: 'var(--navy)', margin: '10px 0 14px' }}>
                    Wat kost {plaag.nl.toLowerCase()}bestrijding?
                  </h2>
                  <p style={{ color: 'var(--text)', lineHeight: 1.7 }}>{deep.costs}</p>
                </div>
                <div className="plaag-price-card">
                  <div style={{ fontSize: '.78rem', color: 'var(--orange-dark)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em' }}>Prijs op aanvraag</div>
                  <div className="plaag-price" style={{ fontSize: '1.55rem', lineHeight: 1.15 }}>Telefonisch vooraf</div>
                  <div className="plaag-price-sub">Eerlijk tarief, geen verrassingen</div>
                  <ul style={{
                    listStyle: 'none', padding: 0, margin: '16px 0 0',
                    display: 'flex', flexDirection: 'column', gap: 8,
                    textAlign: 'left',
                  }}>
                    <li style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: '.85rem', color: 'var(--text)' }}>
                      <span style={{ color: 'var(--green)', fontWeight: 700, flexShrink: 0 }}>✓</span>
                      <span>Definitieve prijs telefonisch vóór de afspraak</span>
                    </li>
                    <li style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: '.85rem', color: 'var(--text)' }}>
                      <span style={{ color: 'var(--green)', fontWeight: 700, flexShrink: 0 }}>✓</span>
                      <span>Gratis telefonisch advies</span>
                    </li>
                    <li style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: '.85rem', color: 'var(--text)' }}>
                      <span style={{ color: 'var(--green)', fontWeight: 700, flexShrink: 0 }}>✓</span>
                      <span>Geen verborgen kosten</span>
                    </li>
                  </ul>
                  <a href={url.offerte()} className="btn-meer" style={{ marginTop: 16 }}>Gratis offerte →</a>
                </div>
                <style>{`@media (max-width:760px){ .kost-grid{ grid-template-columns:1fr !important; } }`}</style>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="section faq-section">
            <div className="container" style={{ maxWidth: 860 }}>
              <div className="sh">
                <div className="section-tag">FAQ</div>
                <h2>Veelgestelde vragen over <em>{plaag.nl.toLowerCase()}bestrijding</em></h2>
              </div>
              <div className="faq-list">
                {getPlaagFaqs(plaag.slug).map((f, i) => (
                  <PlaagFaqItem key={i} q={f.q} a={f.a}
                                open={openFaq === i}
                                onToggle={() => setOpenFaq(openFaq === i ? -1 : i)} />
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* Other plagen, slideshow van alle plagen */}
      <AndereDienstenCarousel excludeSlug={plaag.slug} />

      {/* Final CTA */}
      <section className="cta-block">
        <div className="container">
          <div className="cta-block-inner">
            <h2>Last van {plaag.nl.toLowerCase()}? <em style={{ color: 'var(--orange)', fontStyle: 'normal' }}>Bel direct.</em></h2>
            <p>Onze gecertificeerde specialisten zijn gemiddeld binnen 24 uur bij u. Bereikbaar ma, zo 07:00-22:00, spoed 24/7.</p>
            <div className="cta-block-buttons">
              <a href="tel:0855005050" className="cta-btn cta-btn-primary">
                <IconPhone width="18" height="18" /> 085 500 50 50
              </a>
              <a href={url.offerte()} className="cta-btn cta-btn-secondary">Gratis offerte aanvragen</a>
            </div>
            <div className="cta-block-trust">
              ✓ Gratis advies &nbsp;·&nbsp; ✓ Gecertificeerde partners &nbsp;·&nbsp; ✓ Vakkundige nacontrole
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

